import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";

import PriceFilters from "./PriceFilters";
import PriceTrendChart from "./PriceTrendChart";
import {
  DEFAULT_TREND_FILTER_MODE,
  usePriceTrendData,
  useProductOptions,
  useProvinceOptions,
  useRetailerOptions,
} from "./usePriceReportData";

const titleMap = {
  retailer: "Price / Retailer",
  product: "Price / Product",
  province: "Price / Province",
};

const PriceReportHost = ({
  report = "retailer",
  hideFilters = false,
  trendFilterMode = DEFAULT_TREND_FILTER_MODE,
  sharedTrendRange,
  sharedTrendGranularity,
  sharedTrendFiltersState,
  onSharedTrendRangeChange,
  onSharedTrendGranularityChange,
  onSharedTrendFiltersStateChange,
  retailerFilterOptions,
  productFilterOptions,
  provinceFilterOptions,
  retailerFilterOptionsLoading,
  productFilterOptionsLoading,
  provinceFilterOptionsLoading,
}) => {
  const [granularity, setGranularity] = useState("month");
  const [range, setRange] = useState({
    startDate: dayjs().subtract(6, "month").startOf("month"),
    endDate: dayjs().endOf("day"),
  });
  const [filters, setFilters] = useState({
    retailerValues: [],
    productValues: [],
    provinceValues: [],
  });

  const effectiveRange = hideFilters ? sharedTrendRange : range;
  const effectiveGranularity = hideFilters ? sharedTrendGranularity : granularity;
  const effectiveFilters = hideFilters ? sharedTrendFiltersState : filters;
  const setEffectiveRange = hideFilters ? onSharedTrendRangeChange : setRange;
  const setEffectiveGranularity = hideFilters
    ? onSharedTrendGranularityChange
    : setGranularity;
  const setEffectiveFilters = hideFilters ? onSharedTrendFiltersStateChange : setFilters;

  const { data: retailerOptsInternal = [], isLoading: retailerLoadingInternal } =
    useRetailerOptions(!hideFilters && report === "retailer");
  const { data: productRows = [], isLoading: productLoadingInternal } = useProductOptions(
    !hideFilters && report === "product"
  );
  const { data: provinceOptsInternal = [], isLoading: provinceLoadingInternal } =
    useProvinceOptions(!hideFilters && report === "province");

  const productOptsInternal = useMemo(
    () =>
      productRows
        .filter((x) => x?.id && x?.name)
        .map((x) => ({ value: x.id, label: x.name })),
    [productRows]
  );

  const retailerOpts = hideFilters ? retailerFilterOptions : retailerOptsInternal;
  const productOpts = hideFilters ? productFilterOptions : productOptsInternal;
  const provinceOpts = hideFilters ? provinceFilterOptions : provinceOptsInternal;
  const retailerLoading = hideFilters
    ? retailerFilterOptionsLoading
    : retailerLoadingInternal;
  const productLoading = hideFilters ? productFilterOptionsLoading : productLoadingInternal;
  const provinceLoading = hideFilters
    ? provinceFilterOptionsLoading
    : provinceLoadingInternal;

  const qryFilters = useMemo(
    () => ({
      retailers: effectiveFilters.retailerValues || [],
      products: (effectiveFilters.productValues || []).map((x) => x.value),
      provinces: (effectiveFilters.provinceValues || []).map((x) => x.value),
    }),
    [effectiveFilters]
  );

  const trend = usePriceTrendData({
    report,
    range: effectiveRange,
    granularity: effectiveGranularity,
    filters: qryFilters,
    trendFilterMode,
  });

  return (
    <Box
      sx={{
        width: "100%",
        boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
        position: "relative",
        padding: "16px",
        backgroundColor: "#efeeee",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {titleMap[report]}
        </Typography>
        <Typography sx={{ fontWeight: 300, fontSize: "0.8rem" }}>
          Price history trends using normalized kg unit pricing.
        </Typography>
      </Box>

      {!hideFilters && (
        <PriceFilters
          report={report}
          range={effectiveRange}
          onRangeChange={setEffectiveRange}
          granularity={effectiveGranularity}
          onGranularity={setEffectiveGranularity}
          filters={effectiveFilters}
          onFilters={setEffectiveFilters}
          retailerOpts={retailerOpts}
          productOpts={productOpts}
          provinceOpts={provinceOpts}
          retailerLoading={retailerLoading}
          productLoading={productLoading}
          provinceLoading={provinceLoading}
        />
      )}

      {trend.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load trend data.
        </Alert>
      )}

      {trend.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <PriceTrendChart
          reportLabel={titleMap[report]}
          rows={trend?.data || []}
          granularity={effectiveGranularity}
        />
      )}
    </Box>
  );
};

export default PriceReportHost;
